import { append, GridApiProps } from "../../reusable_components/CygnusGrid/CygnusGrid.utils";
import { makeCygnusApiCall } from "../../../api/api";

/**
 * Fetches engagement processed template page data.
 * @param props pagination and search text
 * @returns Object containing records and total count.
 */
export const fetchEngagementProcessedTemplatePageData = async (
  {
    offset,
    limit,
    searchText,
    sourceCode,
    filter,
  }: GridApiProps
): Promise<{ records: any[]; totalCount: number }> => {  
  const page = Math.floor(offset / limit) + 1;
  const params = new URLSearchParams();

  params.set("page", String(page));
  params.set("page_size", String(limit));
  params.set("search_text", String(searchText));

  Object.entries(filter ?? {}).forEach(([k, v]) => append(k, v, params));

  const url = `/engagement-letter/processed_templates/?${params.toString()}`;

  const response = await makeCygnusApiCall({
    type: "GET",
    url,
    show_message: true,
    customized_success_message: "Fetched Engagement letters successfully.",
    customized_failure_message:
      "Unable to fetch Engagement letters, please try again.",
    customized_error_message:
      "Error occurred while fetching Engagement letters. Please contact support.",
    forMDM: true,
  });

  if (response.success) {
    const records: any = response.data;
    const totalRecords: number = response.total_records || 0;
    return { records, totalCount: totalRecords };
  } else {
    return { records: [], totalCount: 0 };
  }
};

/**
 * Fetches clients group data.
 *
 * @returns data array.
 */

export const fetchClientGroups = async (): Promise<any[]> => {  
  const url = `/dropdown/fetch_options/`;

  const response = await makeCygnusApiCall({
    type: "POST",
    url,
    payload: {
      SelectedApiKey: "get_group_cch"
    },
    show_message: false,
    customized_success_message: "Fetched client groups successfully.",
    customized_failure_message:
      "Unable to fetch client groups, please try again.",
    customized_error_message:
      "Error occurred while fetching client groups. Please contact support.",
    forMDM: true,
  });

  if (response.success) {    
    return response.data ?? [];
  } else {
    return [];
  }
};

/**
 * sends el for approval.
 * @param payload data array containing selected rows
 * @returns data array.
 */
export const sendELForApproval = async (payload: any): Promise<any[]> => {  
  const url = `/engagement-letter/send_el_for_approval/`;

  const response = await makeCygnusApiCall({
    type: "POST",
    url,
    payload: payload,
    show_message: true,
    customized_success_message: "Send for approval successful!",
    customized_failure_message:
      "Send for approval failed.",
    customized_error_message:
      "Error occurred while sending for approval. Please contact support.",
    forMDM: true,
  });

  if (response.success) {    
    return response.su ?? [];
  } else {
    return [];
  }
};

/**
 * sends el for approval.
 * @param payload data array containing selected rows
 * @returns data array.
 */
export const sendProcessedTemplateForSignature = async (payload: any): Promise<any[]> => {  
  const url = `/engagement-letter/send_processed_template_for_signature/`;

  const response = await makeCygnusApiCall({
    type: "POST",
    url,
    payload: payload,
    show_message: true,
    customized_success_message: "Send for signature successful!",
    customized_failure_message:
      "Send for signature failed.",
    customized_error_message:
      "Error occurred while sending for signature. Please contact support.",
    forMDM: true,
  });

  if (response.success) {    
    return response.su ?? [];
  } else {
    return [];
  }
};


export const sendProcessedTemplatesForWetSign = async (payload: {
  ProcessedTemplateIds: number[];
  SignatureStatus: string;
  AutoApprove: boolean;
}): Promise<boolean> => {
  const response = await makeCygnusApiCall({
    type: "PUT",
    url: "/engagement-letter/processed_templates/",
    payload,
    show_message: false,
    customized_success_message: "WetSign update successful!",
    customized_failure_message: "Failed to update WetSign status.",
    customized_error_message: "Error occurred while updating WetSign status.",
    forMDM: true,
  });

  return response?.success ?? false;
};


/**
 * Downloads engagement letters.
 * If 1 EL is selected: downloads a single PDF.
 * If multiple ELs are selected: downloads a ZIP.
 *
 * @param processedTemplateIds Array of ProcessedTemplateIds
 */
export const downloadEngagementLetters = async (
  processedTemplateIds: number[]
): Promise<void> => {
  const url = `/engagement-letter/download_zip/`;

  try {
    const blob = await makeCygnusApiCall({
      type: "POST",
      url,
      payload: { ProcessedTemplateIds: processedTemplateIds },
      isblob: true,
      show_message: false,
      forMDM: true,
    });

    if (!blob) return;

    const filename =
      processedTemplateIds.length === 1
        ? "EngagementLetter.pdf"
        : "EngagementLetters.zip";

    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    link.click();

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Download failed:", error);
  }
};

/**
 * Soft deletes engagement letters.
 * Performs bulk delete by setting Active = 'N' in backend.
 *
 * @param processedTemplateIds Array of ProcessedTemplateIds to delete
 * @returns boolean indicating success or failure
 */
export const deleteProcessedTemplates = async (
  processedTemplateIds: number[],
  deletionReason: string = ""
): Promise<boolean> => {
  const url = `/engagement-letter/processed_templates/`;

  const response = await makeCygnusApiCall({
    type: "DELETE",
    url,
    payload: { ProcessedTemplateIds: processedTemplateIds, DeletionReason: deletionReason },
    show_message: true,
    customized_success_message: "Engagement letters deleted successfully.",
    customized_failure_message:
      "Failed to delete engagement letters. Please try again.",
    customized_error_message:
      "Error occurred while deleting engagement letters. Please contact support.",
    forMDM: true,
  });

  return response?.success ?? false;
};